import { useCallback } from "react";

import { fetchAddressByPostalCode } from "@/lib/fetch-api/postal-code";
import type { AddressObject, InputMaybe, LangCode } from "@/lib/gql/__generated__/graphql";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

type Props = {
	address: ForAutoGenerator<InputMaybe<AddressObject> | undefined, "">;
	onSetViewModel: (
		values: {
			key: "address";
			value: // | ForAutoGenerator<AddressObject, "">
			ForAutoGenerator<
				{
					city: string;
					pref: string;
					town: string;
					nationality: InputMaybe<string> | undefined;
					other: InputMaybe<string> | undefined;
					postalCode: InputMaybe<string> | undefined;
					state: InputMaybe<string> | undefined;
				},
				""
			>;
		}[],
	) => void;

	lang?: LangCode;
};

export const useFormAddressModel = ({ address: addressViewModel, onSetViewModel, lang }: Props) => {
	const onSetPostalCodeBase = useCallback(
		async <K extends keyof Pick<AddressObject, "postalCode">>(
			key: K,
			value: Pick<AddressObject, "postalCode">[K],
		) => {
			const res = await fetchAddressByPostalCode(value, lang);

			if (res?.address?.data) {
				const { address } = res;

				if (address?.data.pref && address?.data.city && address?.data.town) {
					const newValue = {
						postalCode: {
							isEdited: true,
							value: value,
						},
						pref: {
							isEdited: true,
							value: address?.data.pref ?? "",
						},
						town: {
							isEdited: true,
							value: address?.data.town ?? "",
						},
						city: {
							isEdited: true,
							value: address?.data.city ?? "",
						},
					};

					onSetViewModel([
						{
							key: "address",
							value: {
								nationality: {
									isEdited: true,
									value: addressViewModel?.nationality?.value,
								},
								other: {
									isEdited: true,
									value: addressViewModel?.other?.value,
								},
								state: {
									isEdited: true,
									value: addressViewModel?.state?.value,
								},
								...newValue,
							},
						},
					]);
				}

				return;
			}

			const newValue = {
				...addressViewModel,
				value: {
					[key]: {
						isEdited: true,
						value,
					},
				},
			};

			onSetViewModel([
				{
					key: "address",
					value: newValue as ForAutoGenerator<
						{
							city: string;
							pref: string;
							town: string;
							nationality: InputMaybe<string> | undefined;
							other: InputMaybe<string> | undefined;
							postalCode: InputMaybe<string> | undefined;
							state: InputMaybe<string> | undefined;
						},
						""
					>,
				},
			]);
		},
		[addressViewModel, lang, onSetViewModel],
	);

	const onSetAddress = useCallback(
		<K extends keyof Omit<AddressObject, "postalCode">>(key: K, value: Omit<AddressObject, "postalCode">[K]) => {
			const newValue = {
				...addressViewModel,
				value: {
					...addressViewModel,
					[key]: {
						isEdited: true,
						value: value || undefined,
					},
				},
			};

			onSetViewModel([
				{
					key: "address",
					value: newValue as ForAutoGenerator<
						{
							city: string;
							pref: string;
							town: string;
							nationality: InputMaybe<string> | undefined;
							other: InputMaybe<string> | undefined;
							postalCode: InputMaybe<string> | undefined;
							state: InputMaybe<string> | undefined;
						},
						""
					>,
				},
			]);
		},
		[addressViewModel, onSetViewModel],
	);

	const onSetPostalCode = useCallback(
		async (e: string) => {
			await onSetPostalCodeBase("postalCode", e);
		},
		[onSetPostalCodeBase],
	);

	const onSetPref = useCallback(
		(e: string) => {
			onSetAddress("pref", e);
		},
		[onSetAddress],
	);

	const onSetCity = useCallback(
		(e: string) => {
			onSetAddress("city", e);
		},
		[onSetAddress],
	);

	const onSetTown = useCallback(
		(e: string) => {
			onSetAddress("town", e);
		},
		[onSetAddress],
	);

	const isEdited =
		addressViewModel?.city?.isEdited ||
		addressViewModel?.pref?.isEdited ||
		addressViewModel?.postalCode?.isEdited ||
		addressViewModel?.town?.isEdited;

	const hasError = Boolean(
		addressViewModel?.city?.error ||
			addressViewModel?.pref?.error ||
			addressViewModel?.postalCode?.error ||
			addressViewModel?.town?.error,
	);

	return {
		isEdited,
		hasError,

		address: addressViewModel,
		onSetPref,
		onSetCity,
		onSetTown,

		onSetPostalCode,
	};
};
